import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  clearMaintenancedBusFleets,
} from '../../../../actions';
import FormFooter from '../../../common/forms/FormFooter';
import Table from '../../../common/Table';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import Modal from '../../../common/modal/Modal';
import {
  COLOR,
  BUS_MAINTENANCE_STATUS_TYPE,
  DEFAULT_PAGE_SIZE,
} from '../../../../config/constants';
import { generateMaintenanceBusFleetColumnsForBulk } from '../../../../config/columns';
import BusMaintenanceStayForm from '../bus-maintenance-stay/BusMaintenanceStayForm';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_TIME_FORMAT } from '../../../../config/locale';

const generateStyle = (row) => {
  let background = '';

  if (row) {
    const { original: { modified } } = row;

    if (modified) {
      background = COLOR.success;
    }
  }

  return { background };
};

const MaintenanceBusFleetTable = ({
  onHandleBulkSave,
  dispatchClearMaintenancedBusFleets,
  loading,
  data: propsData,
}) => {
  useEffect(
    () => () => {
      dispatchClearMaintenancedBusFleets();
    },
    [],
  );

  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('Editar Estancia');
  const [modalBody, setModalBody] = useState(null);
  const [modifiedData, setModifiedData] = useState([]);

  useEffect(() => {
    const newContent = propsData.map((row) => ({
      ...row,
      modified: false,
      registeredBusIdSelect: row.registeredBusId
        ? {
          value: row.registeredBusId,
          label: row.companyBusId || row.registeredBusId,
        }
        : null,
      originCityIdSelect: row.originCityId
        ? {
          value: row.originCityId,
          label: row.originCityName || row.originCityId,
        }
        : null,
      maintenanceStayStatusSelect: row.maintenanceStayStatus
        ? BUS_MAINTENANCE_STATUS_TYPE[row.maintenanceStayStatus]
        : null,
    }));

    setData(newContent);
    setOriginalData(newContent);
    setModifiedData([]);

  }, [propsData]);

  const onCloseModal = () => {
    setShowModal(false);
    setModalBody(null);
  };

  const getDataIndex = ({ busMaintenanceStayId }) => data.findIndex((row) => row.busMaintenanceStayId === busMaintenanceStayId);

  const onSubmitForm = (formValues) => {
    const index = getDataIndex({ busMaintenanceStayId: formValues.id });
    const newData = [...data];

    newData[index] = {
      ...newData[index],
      registeredBusId: (formValues.registeredBusId && formValues.registeredBusId.value) || formValues.registeredBusId,
      registeredBusIdSelect: formValues.registeredBusId,
      originCityId: (formValues.originCityId && formValues.originCityId.value) || formValues.originCityId,
      originCityIdSelect: formValues.originCityId,
      maintenanceStayStatus: (formValues.maintenanceStayStatus && formValues.maintenanceStayStatus.value) || formValues.maintenanceStayStatus,
      maintenanceStayStatusSelect: formValues.maintenanceStayStatus,
      observation: formValues.observation,
      estimatedArrivalDate: formValues.estimatedArrivalDate,
      estimatedCompletionDate: formValues.estimatedCompletionDate,
      noArrivalTime: formValues.noArrivalTime,
      modified: true,
    };

    const existingIndex = modifiedData.findIndex(
      (item) => item.busMaintenanceStayId === formValues.id,
    );
    if (existingIndex >= 0) {
      const newModifiedData = [...modifiedData];
      newModifiedData[existingIndex] = newData[index];
      setModifiedData(newModifiedData);
    } else {
      setModifiedData([...modifiedData, newData[index]]);
    }

    setData(newData);
    onCloseModal();
  };

  const generateInitialValues = (row) => ({
    id: row.busMaintenanceStayId,
    registeredBusId: row.registeredBusIdSelect || (row.registeredBusId && {
      value: row.registeredBusId,
      label: row.companyBusId || row.registeredBusId,
    }),
    originCityId: row.originCityIdSelect || (row.originCityId && {
      value: row.originCityId,
      label: row.originCityName || row.originCityId,
    }),
    maintenanceStayStatus: row.maintenanceStayStatusSelect ||
      (row.maintenanceStayStatus && BUS_MAINTENANCE_STATUS_TYPE[row.maintenanceStayStatus]),
    observation: row.observation,
    estimatedArrivalDate: row.estimatedArrivalDate != null
      ? tzNormalizeDate({ date: row.estimatedArrivalDate, format: INT_DATE_TIME_FORMAT })
      : null,
    estimatedCompletionDate: row.estimatedCompletionDate != null
      ? tzNormalizeDate({ date: row.estimatedCompletionDate, format: INT_DATE_TIME_FORMAT })
      : null,
    noArrivalTime: row.noArrivalTime || false,
  });

  const handleEdit = (row) => {
    const body = (
      <BusMaintenanceStayForm
        edit
        initialValues={generateInitialValues(row)}
        onCancel={onCloseModal}
        onSubmit={onSubmitForm}
      />
    );

    setModalTitle(`Editar Estancia - Bus ${row.companyBusId || row.busMaintenanceStayId}`);
    setModalBody(body);
    setShowModal(true);
  };

  const handleClearRow = (row) => {
    const index = getDataIndex(row);
    const newData = [...data];
    newData[index] = {
      ...originalData[index],
      modified: false,
    };

    setModifiedData(modifiedData.filter((item) => item.busMaintenanceStayId !== row.busMaintenanceStayId));
    setData(newData);
  };

  const modal = (
    <Modal
      show={showModal}
      title={modalTitle}
      onClickClose={onCloseModal}
      body={modalBody}
      size="lg"
    />
  );

  const saveButtonDisabled = loading || modifiedData.length === 0;

  const handleSaveAll = () => {
    onHandleBulkSave(modifiedData);
  };

  const pendingChangesCount = modifiedData.length;

  const defaultPage = DEFAULT_PAGE_SIZE;

  return (
    <div className="mt-4">
      <Table
        manual={false}
        columns={generateMaintenanceBusFleetColumnsForBulk({
          handleEdit,
          handleClearRow,
        })}
        data={data}
        filterable
        defaultFilterMethod={filterMethodCaseInsensitive}
        loading={loading}
        generateStyle={generateStyle}
        defaultPageSize={defaultPage}
      />

      <div className="mt-3">
        <FormFooter
          saveButtonAction={handleSaveAll}
          saveButtonDisabled={saveButtonDisabled}
          saveButtonText={
            pendingChangesCount > 0
              ? `Guardar Cambios (${pendingChangesCount})`
              : 'Guardar Cambios'
          }
          saveButtonType="button"
        />
      </div>
      {modal}
    </div>
  );
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => ({
  loading: MechanicalMaintenanceUnit.MaintenanceBusFleet.getIn(['all', 'loading']),
  data: MechanicalMaintenanceUnit.MaintenanceBusFleet.getIn(['all', 'content', 'content']) || [],
});

const mapDispatchToProps = {
  dispatchClearMaintenancedBusFleets: clearMaintenancedBusFleets,
};

MaintenanceBusFleetTable.propTypes = {
  loading: PropTypes.bool,
  dispatchClearMaintenancedBusFleets: PropTypes.func.isRequired,
  onHandleBulkSave: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

MaintenanceBusFleetTable.defaultProps = {
  loading: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MaintenanceBusFleetTable);